import { Tooltip } from '@material-ui/core'

import {
  TOJSlotContainer,
  TOJBarItem,
  TOJBarContent,
  TOJBarRing,
  TOJBarPercentageText,
  TOJBarProductType,
  TOJBarVolumeText,
  TOJBarTrack,
  TOJBarFill,
} from './dashboard-jetty.styled'

// All coordinates are in BerthMorowali.svg space (viewBox 0 0 1454 720).
export const MAP_WIDTH = 1454
export const MAP_HEIGHT = 720

// Vessels are moored on the top edge of the pier and grow upward (bow up).
const PIER_TOP_Y = 441

// Centre x of the 9 mooring pads. Berth 1 is the right-most pad (same
// convention as the previous map); change the order here if the numbering differs.
export const BERTH_CENTER_X = [1328.8, 1232.9, 1112.9, 992.9, 897.1, 801.3, 681.3, 561.3, 441.3]
export const BERTH_COUNT = BERTH_CENTER_X.length

// Fuel Bunker (Tanker only) lies alongside the west end of the pier.
const FUEL_BUNKER_CENTER = { x: 353.5, y: 490.5 }

export const FUEL_BUNKER_KEY = 'fuel-bunker'

const SIZE_SCALE = { Small: 0.8, Medium: 0.9, Large: 1 }

// Drawn length (SVG units) of a vessel on a berth, by size only (the same for
// every vessel type). They follow the berth drawing outlines: yellow = Large,
// green = Medium, red = Small (Large = 233 as in "Berth Morowali With Ship.svg").
// Tune here to adjust the vessel length per size.
const BERTH_LENGTH_BY_SIZE = { Small: 188, Medium: 214, Large: 233 }
const FUEL_BUNKER_LARGE_LENGTH = 183
const BERTH_BREADTH = 78
const FUEL_BUNKER_BREADTH = 71

// A Tanker on Berth 5 is drawn horizontally (parallel to the pier) across Berth 4
// and Berth 5: the database rule (get_sr_inprogress_by_srcode) blocks Berth 4
// while a Tanker is in progress on Berth 5.
const TANKER_HORIZONTAL_BERTH = 5

export const parseVessel = (vesselSize) => {
  const [rawType = '', rawSize = ''] = (vesselSize || '').split('|')
  const type = rawType.replace(/[/_ ]/g, '')
  const size = rawSize.replace(/[/_ ]/g, '')
  return { type, size: SIZE_SCALE[size] ? size : 'Large' }
}

// Resolve which map slot a jetty belongs to, by jetty NAME ("Berth 3" -> 3,
// "Fuel Bunker" -> fuel-bunker). Returns null when the name is not recognised.
export const resolveSlotKey = (jettyName) => {
  const name = (jettyName || '').trim()
  if (/fuel\s*bunker/i.test(name)) return FUEL_BUNKER_KEY
  const match = name.match(/berth\s*0*(\d+)/i)
  if (match) {
    const number = parseInt(match[1], 10)
    if (number >= 1 && number <= BERTH_COUNT) return number
  }
  return null
}

// Every slot of the map, in display order: Berth 1..9 then Fuel Bunker.
export const MOROWALI_SLOT_NAMES = [
  ...BERTH_CENTER_X.map((_, i) => `Berth ${i + 1}`),
  'Fuel Bunker',
]

// Slots whose jetty is not in the data (master jetty not available yet).
// Nothing is reported while there is no data (loading / failed request) or when
// no jetty name is recognised at all, so a connection problem is not shown as
// "Not Available" on every slot.
export const getMissingSlots = (dataJetty) => {
  const list = dataJetty || []
  const present = new Set(
    list.map((dt) => resolveSlotKey(dt.Jetty_Name)).filter((key) => key !== null)
  )
  if (present.size === 0) return []
  return MOROWALI_SLOT_NAMES.map((name) => ({ name, key: resolveSlotKey(name) })).filter(
    ({ key }) => !present.has(key)
  )
}

// Sort key to keep the side list in slot order (unknown jetties stay last).
export const slotOrder = (jettyName, fallbackIndex = 0) => {
  const key = resolveSlotKey(jettyName)
  if (key === FUEL_BUNKER_KEY) return BERTH_COUNT + 1
  if (key !== null) return key
  return BERTH_COUNT + 2 + fallbackIndex
}

// Geometry of one vessel: centre point, drawn length/breadth (SVG units).
// The breadth is the same for every size and type (the berth outlines have the
// same width); only the length changes with the size.
export const getVesselGeometry = (slotKey, vesselSize) => {
  const { type, size } = parseVessel(vesselSize)

  if (slotKey === FUEL_BUNKER_KEY) {
    const drawnLength = FUEL_BUNKER_LARGE_LENGTH * SIZE_SCALE[size]
    return { ...FUEL_BUNKER_CENTER, length: drawnLength, breadth: FUEL_BUNKER_BREADTH, type, size }
  }

  if (type === 'Tanker' && slotKey === TANKER_HORIZONTAL_BERTH) {
    const drawnLength = FUEL_BUNKER_LARGE_LENGTH * SIZE_SCALE[size]
    return {
      x: (BERTH_CENTER_X[slotKey - 2] + BERTH_CENTER_X[slotKey - 1]) / 2,
      y: PIER_TOP_Y - FUEL_BUNKER_BREADTH / 2,
      length: drawnLength,
      breadth: FUEL_BUNKER_BREADTH,
      horizontal: true,
      type,
      size,
    }
  }

  const drawnLength = BERTH_LENGTH_BY_SIZE[size]
  return {
    x: BERTH_CENTER_X[slotKey - 1],
    y: PIER_TOP_Y - drawnLength / 2,
    length: drawnLength,
    breadth: BERTH_BREADTH,
    type,
    size,
  }
}

// Dimmed pad + "<name> Not Available" tag for a slot without a master jetty.
export const NotAvailableMark = ({ slotKey, name }) => {
  // Fuel Bunker is drawn as an HTML tag (FuelBunkerNotAvailableTag), centred
  // between the side panel and the pier; only the berths are drawn here.
  if (slotKey === FUEL_BUNKER_KEY) return null
  const cx = BERTH_CENTER_X[slotKey - 1]
  // covers the blue name plate of the berth (x - 27.7 .. x + 27.7, y 503.84 .. 526.23)
  // so the berth name is not shown twice
  const plateWidth = 58
  const plateHeight = 25.2
  const plateY = 502.5

  return (
    <g>
      <rect x={cx - 27.8} y={440.5} width={55.6} height={53.2} fill='#3b4650' fillOpacity={0.6} />
      <rect
        x={cx - plateWidth / 2}
        y={plateY}
        width={plateWidth}
        height={plateHeight}
        rx={2}
        fill='#5f6b73'
        stroke='#ffffff'
        strokeOpacity={0.7}
        strokeWidth={0.64}
      />
      <text x={cx} y={plateY + 10.5} textAnchor='middle' fontSize={8.5} fontWeight={700} fill='#ffffff'>
        {name}
      </text>
      <text x={cx} y={plateY + 20} textAnchor='middle' fontSize={7.5} fill='#ffffff'>
        Not Available
      </text>
    </g>
  )
}

export const toPercentX = (x) => `${(x / MAP_WIDTH) * 100}%`
export const toPercentY = (y) => `${(y / MAP_HEIGHT) * 100}%`

export const RenderTOJSlot = ({ data, handleClick, geometry }) => {
  const item = data?.Child?.[0]
  if (!item) return null
  const percentage = item.Total_Progress_Activity > 100 ? 100 : item.Total_Progress_Activity

  return (
    <TOJSlotContainer left={toPercentX(geometry.x)} top={toPercentY(geometry.y)}>
      <TOJBarItem onClick={() => handleClick(item)}>
        <TOJBarTrack>
          <TOJBarFill percentage={percentage} />
        </TOJBarTrack>
        <TOJBarContent>
          <TOJBarRing percentage={percentage}>
            <TOJBarPercentageText>{percentage}%</TOJBarPercentageText>
          </TOJBarRing>
          <Tooltip title={item.Product_Type} placement='top'>
            <TOJBarProductType>{item.Product_Type}</TOJBarProductType>
          </Tooltip>
          <TOJBarVolumeText>{item.Volume} / {item.Quantity}</TOJBarVolumeText>
        </TOJBarContent>
      </TOJBarItem>
    </TOJSlotContainer>
  )
}

// ---------------------------------------------------------------------------
// Dummy data (dev only, enable with ?dummyJetty=1) to preview a full map.
// Same shape as GET /jetty_maps items.
// ---------------------------------------------------------------------------
export const isDummyJettyEnabled = () => {
  if (typeof window === 'undefined') return false
  // never on a deployed site: only in dev mode, or a production build on localhost
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname)
  if (process.env.NODE_ENV === 'production' && !isLocalhost) return false
  return new URLSearchParams(window.location.search).get('dummyJetty') === '1'
}

export const buildDummyJettyData = () => {
  const berthVessels = []
  const types = ['Bulk Barge', 'Barge']
  const sizes = ['Small', 'Medium', 'Large']
  for (let i = 0; i < BERTH_COUNT; i++) {
    berthVessels.push(`${types[i % 2]}|${sizes[i % 3]}`)
  }
  // By default a Tanker is on Berth 5 (drawn horizontally across Berth 4 and 5) and
  // Berth 4 is left empty, as it is blocked while that Tanker is in progress.
  // Add &dummyTanker=0 to fill every berth with a barge instead.
  const tankerOnBerth5 = new URLSearchParams(window.location.search).get('dummyTanker') !== '0'
  if (tankerOnBerth5) berthVessels[4] = 'Tanker|Large'
  const makeJetty = (name, vesselSize, index) => ({
    Jetty_Name: name,
    Location: 'Morowali',
    Length: 0,
    Detail: [
      {
        position: 0,
        Jetty_Name: name,
        Location: 'Morowali',
        Ata: '2025-04-17T08:00:00Z',
        Plan_Depart: '2025-04-20T08:00:00Z',
        Indicator_Delay: ['#8EBB3C', '#F2C300', '#FF0000'][index % 3],
        Vessel_Size: vesselSize,
        Vessel_Name: `DUMMY ${name}`,
        Client_Name: 'Dummy Client',
        Agent_Name: 'Dummy Agent',
        SrNo: `DUMMY-${index + 1}`,
        Loa: 100 + index * 10,
        Actual_Berth_Dur: 3600 * (index + 1),
        Value: 'In Progress',
        // Tankers have no TOJ (product / progress) card in the dummy data
        Child: vesselSize.startsWith('Tanker')
          ? []
          : [
            {
              Product_Type: 'Nickel Ore',
              Volume: 1000 * (index + 1),
              Quantity: 10000,
              Total_Progress_Activity: ((index * 13) % 100) + 5,
            },
          ],
      },
    ],
  })
  // ?dummyMissing=6,7 (or fuel) removes those jetties to preview "Not Available"
  const missing = (new URLSearchParams(window.location.search).get('dummyMissing') || '')
    .split(',')
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean)
  const isMissing = (name) =>
    missing.some((m) => (m === 'fuel' ? name === 'Fuel Bunker' : name === `Berth ${m}`))

  return [
    ...berthVessels
      .map((vs, i) => makeJetty(`Berth ${i + 1}`, vs, i))
      // Berth 4 stays as a jetty but has no vessel (blocked by the Tanker on Berth 5)
      .map((jetty, i) => (tankerOnBerth5 && i === 3 ? { ...jetty, Detail: [] } : jetty)),
    makeJetty('Fuel Bunker', 'Tanker|Large', BERTH_COUNT),
  ].filter((jetty) => !isMissing(jetty.Jetty_Name))
}
